import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { formatDate } from "@/lib/dates";
import { checkUrgency, statusLabel, summarizeChecks, type CheckStatus, type VehicleCheckRow } from "@/lib/vehicleChecks";
import { loadVehicleChecks } from "@/lib/vehicleChecksData";
import { crmFieldsFromTaxiCaller, taxiCallerSummary, type TaxiCallerAccountRow } from "@/lib/crmPrefill";
import {
  Loader2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ClipboardCheck,
  Camera,
  Clock3,
  FileWarning,
  CircleCheckBig,
  CalendarX2,
  Wrench,
  Users,
  Car,
  ShieldAlert,
  Building2,
  X,
  RefreshCw,
  BadgeDollarSign,
  Siren,
  Smile,
} from "lucide-react";
import CRMAccountForm, { type CRMAccount } from "./CRMAccountForm";
import SettingsSidebarLayout from "./ui/settings-sidebar-layout";
import { navigateAdmin, openCrmAccount } from "@/lib/adminNav";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type NullJobRow = {
  driver_name: string | null;
  pu_date: string | null;
  Job_ID: string | null;
  fleet?: string | null;
  Stage?: string | null;
};

// A cancelled or no-show job is never closed out with a payment, so it can't
// be "open" and doesn't count as a job to reconcile either. This is the same
// rule the driver's own Uncomplete Jobs list uses (JobUploadSection.tsx), so
// the two always agree.
const isCancelledOrNoShow = (stage: string | null | undefined) => stage === "Cancelled" || stage === "No show";

type DcpRow = {
  driver_name: string | null;
  reconciled: boolean | null;
  driver_paid: boolean | null;
};

type DriverBucket = {
  driver_name: string;
  totalJobs: number;
  openJobs: number;
  dcpJobs: number;
  reconciledJobs: number;
  paidJobs: number;
  openRows: Array<{ pu_date: string; Job_ID: string }>;
};

type DriverInfo = {
  driver_name: string;
  is_active: boolean;
  include_in_reports: boolean;
};

type VehicleRow = {
  vehicle_number: string;
  status: string | null;
  service_booked_for: string | null;
  cof_booked_for: string | null;
  tyre_rotation_booked_for: string | null;
};

type InspectionRow = {
  id: string | number;
  vehicle_number: string;
  overall_status: string;
  driver_name: string | null;
  inspection_type: string | null;
  created_at: string;
};

type InspectionIssueItemRow = {
  id: string | number;
  inspection_id: string | number;
  vehicle_number: string;
  driver_name: string | null;
  created_at: string;
  category: string;
  label: string;
  status: string;
  note: string;
};

type TimeOffRow = {
  id: string | number;
  driver_name: string;
  request_type: string;
  start_date: string;
  end_date: string;
  status: string;
  reason: string | null;
};

type UnavailRow = {
  driver_name: string;
  day_of_week: number;
};

type FleetIssues = {
  inspectionUnsafe: InspectionIssueItemRow[];
  inspectionFixSoon: InspectionIssueItemRow[];
  inspectionAttention: InspectionIssueItemRow[];
  maintenanceNow: VehicleRow[];
  serviceThisWeek: VehicleRow[];
  cofThisWeek: VehicleRow[];
  tyreThisWeek: VehicleRow[];
};

type RosterIssues = {
  pendingTimeOff: TimeOffRow[];
  approvedTimeOff: TimeOffRow[];
  unavail: UnavailRow[];
  weekStartIso: string;
  weekEndIso: string;
};

type AccountApprovalRow = {
  account_id: string;
  account_name: string;
  /** ISO date (YYYY-MM-DD) -- crm_accounts.active_to is a timestamptz, but
   *  only the date part matters for a days-remaining count. */
  active_to: string;
};

type PermBookingExpiryRow = {
  id: string;
  account_id: string;
  /** Looked up from crm_accounts client-side -- crm_permanent_bookings only
   *  stores the account_id code, not the name. */
  account_name: string;
  booking_name: string;
  expiry_date: string;
};

type ClosedCheckTab =
  | "action"
  | "open"
  | "closedNotReconciled"
  | "fullyReconciled"
  | "noJobs"
  | "shiftIssues"
  | "newAccounts"
  | "accountUpdates"
  | "incidentReports";

interface PendingCrmAccount {
  id: string;
  account_id: string;
  sample_job_id: string | null;
  sample_passenger_name: string | null;
  first_seen_at: string;
  taxicaller_details: TaxiCallerAccountRow | null;
}

interface AccountFieldChange {
  id: string;
  account_id: string;
  field_name: string;
  dms_value: string | null;
  taxicaller_value: string | null;
  first_seen_at: string;
}

interface ShiftFlagRow {
  id: string;
  driver_name: string;
  shift_id: string | null;
  vehicle_number: string | null;
  flag_type: string;
  detail: string;
  created_at: string;
}

interface PendingIncidentRow {
  id: string;
  driver_name: string;
  vehicle_number: string | null;
  incident_category: string | null;
  incident_date: string | null;
  narrative: string | null;
  created_at: string;
}

const SHIFT_FLAG_LABELS: Record<string, string> = {
  too_many_open_shifts: "Too Many Open Shifts",
  shift_open_too_long: "Shift Open Too Long",
  missing_start_odometer: "Missing Start Odometer",
};

const ACCOUNT_FIELD_LABELS: Record<string, string> = {
  account_name: "Account name",
  phone: "Phone",
  is_active: "Active status",
};

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

const EXCLUDED_FLEET = "stss";
const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Normalise a driver name so that records coming from different sources
 * (the `drivers` table vs `taxicaller_data`) can be matched reliably.
 *
 * Without this, an exact / case-sensitive `Set.has()` lookup means that any
 * difference in casing, extra/double spaces or trailing whitespace causes a
 * driver's jobs to NOT be attributed to them — so they wrongly fall into the
 * "No Jobs This Week" bucket even though they have jobs.
 */
const normalizeName = (name: string | null | undefined): string =>
  (name ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const fmtIsoLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const startOfWeekMon = (d: Date) => {
  const x = new Date(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfWeekMonToSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

/** Whole days between todayIso and dateIso (both YYYY-MM-DD) -- negative
 *  once dateIso is in the past. Strips any time component first since
 *  crm_accounts.active_to is a timestamptz but crm_permanent_bookings.
 *  expiry_date is a plain date. */
const daysRemaining = (dateIso: string, todayIso: string): number =>
  Math.round(
    (new Date(`${dateIso.split("T")[0]}T00:00:00`).getTime() - new Date(`${todayIso}T00:00:00`).getTime()) / 86400000
  );

const expiryDaysLabel = (days: number): string =>
  days < 0 ? `expired ${Math.abs(days)}d ago` : days === 0 ? "expires today" : `${days}d left`;

type IssueGroup = {
  key: string;
  header: string;
  items: { key: string; label: string }[];
};

/** Groups flat rows into {header, items} groups keyed by keyFn, preserving each group's
 * first-seen order from the input array -- so pre-sorting rows controls display order. Shared by
 * Fleet Issues (grouped by inspection), Service alerts (grouped by vehicle) and Roster alerts
 * (grouped by driver) so all three panels show one header with the specific issues listed
 * underneath, instead of repeating the header once per issue. */
function groupRows<T>(
  rows: T[],
  keyFn: (row: T) => string,
  headerFn: (row: T) => string,
  itemFn: (row: T) => { key: string; label: string }
): IssueGroup[] {
  const map = new Map<string, IssueGroup>();
  for (const row of rows) {
    const k = keyFn(row);
    let group = map.get(k);
    if (!group) {
      group = { key: k, header: headerFn(row), items: [] };
      map.set(k, group);
    }
    group.items.push(itemFn(row));
  }
  return Array.from(map.values());
}

const groupIssueItems = (rows: InspectionIssueItemRow[]): IssueGroup[] =>
  groupRows(
    [...rows].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    (r) => String(r.inspection_id),
    (r) =>
      `${r.vehicle_number} — ${formatDate(r.created_at)}${
        r.driver_name ? ` — ${r.driver_name}` : ""
      }`,
    (r) => ({
      key: String(r.id),
      label: `${r.category.toUpperCase()} — ${r.label}${r.note.trim() ? `: ${r.note.trim()}` : ""}`,
    })
  );

/** Merges service/COF/tyre-rotation bookings into one group per vehicle, so a vehicle with several
 * bookings the same week shows together instead of scattered across separate lists. */
const groupServiceBookings = (
  serviceRows: VehicleRow[],
  cofRows: VehicleRow[],
  tyreRows: VehicleRow[]
): IssueGroup[] => {
  const combined = [
    ...serviceRows.map((v) => ({
      vehicle_number: v.vehicle_number,
      sortDate: v.service_booked_for || "",
      label: `SERVICE — ${formatDate(v.service_booked_for)}`,
    })),
    ...cofRows.map((v) => ({
      vehicle_number: v.vehicle_number,
      sortDate: v.cof_booked_for || "",
      label: `COF — ${formatDate(v.cof_booked_for)}`,
    })),
    ...tyreRows.map((v) => ({
      vehicle_number: v.vehicle_number,
      sortDate: v.tyre_rotation_booked_for || "",
      label: `TYRE ROTATION — ${formatDate(v.tyre_rotation_booked_for)}`,
    })),
  ].sort((a, b) => (a.sortDate < b.sortDate ? -1 : 1));

  return groupRows(
    combined,
    (r) => r.vehicle_number,
    (r) => r.vehicle_number,
    (r) => ({ key: `${r.vehicle_number}-${r.label}`, label: r.label })
  );
};

/** Merges pending/approved time-off requests and unavailability rules into one group per driver
 * -- a driver with several roster alerts shows together instead of scattered across separate
 * lists. Multiple unavailable days for the same driver collapse into one "Mon, Wed" line. */
const groupRosterAlerts = (
  pendingTimeOff: TimeOffRow[],
  approvedTimeOff: TimeOffRow[],
  unavail: UnavailRow[]
): IssueGroup[] => {
  const unavailByDriver = new Map<string, string[]>();
  for (const u of unavail) {
    const days = unavailByDriver.get(u.driver_name) || [];
    days.push(DOW_NAMES[u.day_of_week] || "Day");
    unavailByDriver.set(u.driver_name, days);
  }

  const combined = [
    ...pendingTimeOff.map((t) => ({
      driver_name: t.driver_name,
      label: `PENDING TIME OFF — ${t.request_type} ${formatDate(t.start_date)} to ${formatDate(
        t.end_date
      )}`,
    })),
    ...approvedTimeOff.map((t) => ({
      driver_name: t.driver_name,
      label: `APPROVED TIME OFF — ${formatDate(t.start_date)} to ${formatDate(t.end_date)}`,
    })),
    ...Array.from(unavailByDriver.entries()).map(([driver_name, days]) => ({
      driver_name,
      label: `UNAVAILABLE — ${days.join(", ")}`,
    })),
  ];

  return groupRows(
    combined,
    (r) => r.driver_name,
    (r) => r.driver_name,
    (r) => ({ key: `${r.driver_name}-${r.label}`, label: r.label })
  );
};

const isFullyReconciled = (bucket: DriverBucket) =>
  bucket.openJobs === 0 &&
  bucket.totalJobs > 0 &&
  bucket.dcpJobs > 0 &&
  bucket.dcpJobs === bucket.reconciledJobs;

const isClosedNotReconciled = (bucket: DriverBucket) =>
  bucket.openJobs === 0 && bucket.totalJobs > 0 && !isFullyReconciled(bucket);

// "Closed and paid" -- every job for this driver's week is done (no open
// jobs) and every completed-payment job has driver_paid = true (set via the
// Weekly Payout Workspace's "Mark prior jobs as paid" / banking file flow).
// Used to grey out and flag driver tiles that need no further action at all.
const isClosedAndPaid = (bucket: DriverBucket) =>
  bucket.openJobs === 0 && bucket.dcpJobs > 0 && bucket.paidJobs === bucket.dcpJobs;

const hasNoJobsThisWeek = (bucket: DriverBucket) =>
  bucket.totalJobs === 0 && bucket.dcpJobs === 0;

const ClosedCheck: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) only ever sees their linked driver(s)/vehicle(s) here.
  // activeDrivers is the source the 5 driver-reconciliation-bucket tabs
  // derive from; Shift Issues and Incident Reports scope their own
  // queries by driver/vehicle below; New Accounts and Account Updates
  // are pure CRM data with no driver/vehicle tie, so they're hidden
  // entirely for a restricted user instead. Unrestricted (the default --
  // everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;

  const [anchorDate, setAnchorDate] = useState<Date>(() =>
    startOfWeekMon(new Date())
  );

  const weekStart = useMemo(() => startOfWeekMon(anchorDate), [anchorDate]);
  const weekEnd = useMemo(() => endOfWeekMonToSun(anchorDate), [anchorDate]);

  const [activeTab, setActiveTab] = useState<ClosedCheckTab>("action");

  // New Accounts / Account Updates are hidden from a restricted user's tab
  // list above -- redirect away if stale state ever lands on one directly.
  useEffect(() => {
    if (scopedDriverNames && (activeTab === "newAccounts" || activeTab === "accountUpdates")) {
      setActiveTab("action");
    }
  }, [scopedDriverNames, activeTab]);

  const [loading, setLoading] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [openRows, setOpenRows] = useState<NullJobRow[]>([]);
  const [allRows, setAllRows] = useState<NullJobRow[]>([]);
  const [dcpRows, setDcpRows] = useState<DcpRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeDrivers, setActiveDrivers] = useState<Set<string>>(new Set());
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [fleetIssues, setFleetIssues] = useState<FleetIssues | null>(null);
  const [rosterIssues, setRosterIssues] = useState<RosterIssues | null>(null);
  const [accountApprovals, setAccountApprovals] = useState<AccountApprovalRow[]>([]);
  const [permBookingExpiries, setPermBookingExpiries] = useState<PermBookingExpiryRow[]>([]);
  const [pendingAccounts, setPendingAccounts] = useState<PendingCrmAccount[]>([]);
  const [pendingAccountsLoading, setPendingAccountsLoading] = useState(false);
  const [formAccount, setFormAccount] = useState<CRMAccount | null>(null);
  const [accountFieldChanges, setAccountFieldChanges] = useState<AccountFieldChange[]>([]);
  const [accountFieldChangesLoading, setAccountFieldChangesLoading] = useState(false);
  const [checkingAccountUpdates, setCheckingAccountUpdates] = useState(false);
  const [shiftFlags, setShiftFlags] = useState<ShiftFlagRow[]>([]);
  const [shiftFlagsLoading, setShiftFlagsLoading] = useState(false);
  const [refreshingShiftFlags, setRefreshingShiftFlags] = useState(false);
  const [pendingIncidents, setPendingIncidents] = useState<PendingIncidentRow[]>([]);
  const [pendingIncidentsLoading, setPendingIncidentsLoading] = useState(false);
  // Admin inspections and camera checks that are overdue or coming due (see src/lib/vehicleChecks.ts).
  const [vehicleChecks, setVehicleChecks] = useState<VehicleCheckRow[]>([]);
  const [checksLoading, setChecksLoading] = useState(false);

  const loadShiftFlags = async () => {
    setShiftFlagsLoading(true);
    let query = supabase
      .from("driver_shift_flags")
      .select("id, driver_name, shift_id, vehicle_number, flag_type, detail, created_at")
      .order("driver_name", { ascending: true });
    if (scopedDriverNames) {
      query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
    }
    const { data, error } = await query;
    if (error) {
      console.error("Error loading shift flags:", error);
    } else {
      setShiftFlags((data as ShiftFlagRow[]) || []);
    }
    setShiftFlagsLoading(false);
  };

  const handleRefreshShiftFlags = async () => {
    setRefreshingShiftFlags(true);
    try {
      const { error } = await supabase.rpc("flag_problem_shifts");
      if (error) {
        toast({ title: "Error", description: "Failed to refresh: " + error.message, variant: "destructive" });
        return;
      }
      await loadShiftFlags();
    } finally {
      setRefreshingShiftFlags(false);
    }
  };

  // Driver-submitted incident reports (src/pages/ReportIncident.tsx) sitting
  // at "submitted_for_review" -- i.e. seen by nobody yet. Disappears from
  // here the moment an admin reviews it in Insurance -> Incidents (which
  // flips status to "reviewed"), so this is naturally just the queue.
  const loadPendingIncidents = async () => {
    setPendingIncidentsLoading(true);
    const { data, error } = await supabase
      .from("insurance_incidents")
      .select("id, driver_name, vehicle_number, incident_category, incident_date, narrative, created_at")
      .eq("status", "submitted_for_review")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error loading pending incident reports:", error);
    } else {
      // Matches if EITHER the driver or the vehicle is in scope -- an
      // incident tied to "their" vehicle but a different driver at the
      // wheel is still relevant, same as Insurance -> Incidents.
      let rows = (data as PendingIncidentRow[]) || [];
      if (scopedDriverNames || scopedVehicleNumbers) {
        const dSet = scopedDriverNames ? new Set(scopedDriverNames) : null;
        const vSet = scopedVehicleNumbers ? new Set(scopedVehicleNumbers) : null;
        rows = rows.filter(
          (r) => (dSet && r.driver_name && dSet.has(r.driver_name)) || (vSet && r.vehicle_number && vSet.has(r.vehicle_number))
        );
      }
      setPendingIncidents(rows);
    }
    setPendingIncidentsLoading(false);
  };

  const loadPendingAccounts = async () => {
    setPendingAccountsLoading(true);
    const { data, error } = await supabase
      .from("crm_account_pending_creations")
      .select("id, account_id, sample_job_id, sample_passenger_name, first_seen_at, taxicaller_details")
      .eq("status", "pending")
      .order("first_seen_at", { ascending: false });
    if (error) {
      console.error("Error loading pending CRM accounts:", error);
    } else {
      setPendingAccounts((data as PendingCrmAccount[]) || []);
    }
    setPendingAccountsLoading(false);
  };

  const handleCreateAccount = async (pending: PendingCrmAccount) => {
    const { data, error } = await supabase
      .from("crm_accounts")
      .insert({
        account_id: pending.account_id,
        account_name: `Unnamed - ${pending.account_id}`,
        tenant_id: DEFAULT_TENANT_ID,
        is_active: true,
        // Name, phone, billing email, Billed/Direct, required fields and the
        // series type -- whatever TaxiCaller's account report told us.
        ...crmFieldsFromTaxiCaller(pending.account_id, pending.taxicaller_details),
      })
      .select("*")
      .single();
    if (error) {
      toast({ title: "Error", description: "Failed to create account: " + error.message, variant: "destructive" });
      return;
    }
    await supabase.from("crm_account_pending_creations").delete().eq("id", pending.id);
    setPendingAccounts((prev) => prev.filter((p) => p.id !== pending.id));
    setFormAccount(data as CRMAccount);
  };

  const handleDismissAccount = async (pending: PendingCrmAccount) => {
    const { error } = await supabase
      .from("crm_account_pending_creations")
      .update({ status: "dismissed", reviewed_at: new Date().toISOString() })
      .eq("id", pending.id);
    if (error) {
      toast({ title: "Error", description: "Failed to dismiss: " + error.message, variant: "destructive" });
      return;
    }
    setPendingAccounts((prev) => prev.filter((p) => p.id !== pending.id));
  };

  const loadAccountFieldChanges = async () => {
    setAccountFieldChangesLoading(true);
    const { data, error } = await supabase
      .from("crm_account_field_changes")
      .select("id, account_id, field_name, dms_value, taxicaller_value, first_seen_at")
      .eq("status", "pending")
      .order("account_id");
    if (error) {
      console.error("Error loading account field changes:", error);
    } else {
      setAccountFieldChanges((data as AccountFieldChange[]) || []);
    }
    setAccountFieldChangesLoading(false);
  };

  // New Accounts is also populated live by a DB trigger the moment a job
  // comes in for an unrecognised account code (see migration 0021) -- but
  // that only ever catches an account once it's had a booking. Refreshing
  // either tab re-runs check-taxicaller-account-changes live against
  // TaxiCaller's full account list (normally only run once a day, 23:59 NZ),
  // which both flags account-detail drift (Account Updates) AND queues any
  // TaxiCaller account with zero bookings yet, so a brand-new account shows
  // up here even before its first job.
  const handleRunAccountSync = async () => {
    setCheckingAccountUpdates(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-taxicaller-account-changes");
      if (error || data?.ok === false) {
        toast({
          title: "Account sync check failed",
          description: error?.message ?? data?.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Account sync check complete",
        description: `${data?.new_accounts_queued ?? 0} new account(s) found, ${data?.fields_flagged ?? 0} difference(s) flagged, ${data?.fields_cleared ?? 0} cleared.`,
      });
      await Promise.all([loadAccountFieldChanges(), loadPendingAccounts()]);
    } catch (e: any) {
      toast({ title: "Account sync check failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setCheckingAccountUpdates(false);
    }
  };

  const handleApproveAccountUpdate = async (change: AccountFieldChange) => {
    const value: unknown =
      change.field_name === "is_active" ? change.taxicaller_value === "true" : change.taxicaller_value;
    const { error: updateErr } = await supabase
      .from("crm_accounts")
      .update({ [change.field_name]: value })
      .eq("account_id", change.account_id);
    if (updateErr) {
      toast({ title: "Error", description: "Failed to update account: " + updateErr.message, variant: "destructive" });
      return;
    }
    await supabase.from("crm_account_field_changes").delete().eq("id", change.id);
    setAccountFieldChanges((prev) => prev.filter((c) => c.id !== change.id));
  };

  const handleKeepOriginalAccountValue = async (change: AccountFieldChange) => {
    const { error } = await supabase
      .from("crm_account_field_changes")
      .update({ status: "dismissed", reviewed_at: new Date().toISOString() })
      .eq("id", change.id);
    if (error) {
      toast({ title: "Error", description: "Failed to dismiss: " + error.message, variant: "destructive" });
      return;
    }
    setAccountFieldChanges((prev) => prev.filter((c) => c.id !== change.id));
  };

  const handleDatePick = (iso: string) => {
    if (!iso) return;
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    if (!Number.isNaN(date.getTime())) setAnchorDate(startOfWeekMon(date));
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setAnchorDate(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setAnchorDate(d);
  };

  const loadActiveDrivers = async (): Promise<Set<string>> => {
    try {
      let query = supabase
        .from("drivers")
        .select("driver_name, is_active, include_in_reports")
        .eq("is_active", true)
        .eq("include_in_reports", true)
        .order("driver_name", { ascending: true });
      if (scopedDriverNames) query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      const { data, error } = await query;

      if (error) throw error;

      const driverSet = new Set<string>(
        (data ?? [])
          .map((d: DriverInfo) => (d.driver_name ?? "").trim())
          .filter(Boolean)
      );

      setActiveDrivers(driverSet);
      return driverSet;
    } catch (e) {
      console.error("Failed to load drivers:", e);
      return new Set();
    }
  };

  const loadActionRequired = async () => {
    try {
      setIssuesLoading(true);

      const now = new Date();
      const monday = startOfWeekMon(now);
      const sunday = endOfWeekMonToSun(now);
      const horizon = new Date(now);
      horizon.setDate(now.getDate() + 30);
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setDate(now.getDate() - 30);

      const weekStartIso = fmtIsoLocal(monday);
      const weekEndIso = fmtIsoLocal(sunday);
      const todayIso = fmtIsoLocal(now);
      const horizonIso = fmtIsoLocal(horizon);
      const oneMonthAgoIso = oneMonthAgo.toISOString();

      // Fleet/roster alerts scope to the linked vehicle(s)/driver(s); the
      // CRM account approvals and permanent booking expiries below are
      // whole-of-company data with no driver/vehicle tie -- skipped
      // entirely for a restricted user rather than shown unfiltered.
      let inspQuery = supabase
        .from("vehicle_inspections")
        .select(
          "id, vehicle_number, overall_status, driver_name, created_at, inspection_type"
        )
        .neq("overall_status", "OK")
        // Fleet Issues is meant to surface what needs attention now, not
        // every unresolved inspection since day one -- without this,
        // more-frequent inspections just meant a permanently-growing
        // wall of old entries. Resolving an item (Mark OK) still removes
        // it immediately; this only caps how far back unresolved ones
        // are dredged up from.
        .gte("created_at", oneMonthAgoIso)
        .order("created_at", { ascending: false });
      let vehQuery = supabase
        .from("vehicles")
        .select(
          "vehicle_number, status, service_booked_for, cof_booked_for, tyre_rotation_booked_for"
        );
      let pendQuery = supabase
        .from("roster_time_off_requests")
        .select(
          "id, driver_name, request_type, start_date, end_date, status, reason"
        )
        .eq("status", "pending")
        .order("start_date", { ascending: true });
      let apprQuery = supabase
        .from("roster_time_off_requests")
        .select(
          "id, driver_name, request_type, start_date, end_date, status, reason"
        )
        .eq("status", "approved")
        .lte("start_date", horizonIso)
        .gte("end_date", todayIso)
        .order("start_date", { ascending: true });
      let unavailQuery = supabase
        .from("roster_driver_availability")
        .select("driver_name, day_of_week, is_available")
        .eq("is_available", false);
      if (scopedVehicleNumbers) {
        const scopedV = scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"];
        inspQuery = inspQuery.in("vehicle_number", scopedV);
        vehQuery = vehQuery.in("vehicle_number", scopedV);
      }
      if (scopedDriverNames) {
        const scopedD = scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"];
        pendQuery = pendQuery.in("driver_name", scopedD);
        apprQuery = apprQuery.in("driver_name", scopedD);
        unavailQuery = unavailQuery.in("driver_name", scopedD);
      }
      const noRowsResult = Promise.resolve({ data: [] as never[], error: null });

      const [inspRes, vehRes, pendRes, apprRes, unavailRes, acctApprovalRes, permBookingRes] =
        await Promise.all([
          inspQuery,
          vehQuery,
          pendQuery,
          apprQuery,
          unavailQuery,
          // Approval expired or expiring within the 30-day horizon --
          // active_to is a timestamptz, compared against horizonIso below.
          scopedDriverNames || scopedVehicleNumbers
            ? noRowsResult
            : supabase
                .from("crm_accounts")
                .select("account_id, account_name, active_to")
                .eq("is_active", true)
                .not("active_to", "is", null)
                .lte("active_to", horizonIso)
                .order("active_to", { ascending: true }),
          scopedDriverNames || scopedVehicleNumbers
            ? noRowsResult
            : supabase
                .from("crm_permanent_bookings")
                .select("id, account_id, booking_name, expiry_date")
                .eq("is_active", true)
                .not("expiry_date", "is", null)
                .lte("expiry_date", horizonIso)
                .order("expiry_date", { ascending: true }),
        ]);

      const inspections = (inspRes.data || []) as InspectionRow[];
      const vehicles = (vehRes.data || []) as VehicleRow[];

      // Pull the actual failed items behind each non-OK inspection so Fleet
      // Issues can show exactly what's wrong (e.g. "SAFETY: Warning Triangle")
      // instead of just repeating the vehicle/driver once per inspection.
      let issueItems: InspectionIssueItemRow[] = [];
      const inspectionIds = inspections.map((i) => i.id);
      if (inspectionIds.length > 0) {
        const { data: itemsData } = await supabase
          .from("vehicle_inspection_items")
          .select("id, inspection_id, category, label, status, note")
          .in("inspection_id", inspectionIds)
          .neq("status", "OK");

        const inspectionById = new Map(inspections.map((i) => [i.id, i]));
        issueItems = (itemsData || []).map((it) => {
          const insp = inspectionById.get(it.inspection_id);
          return {
            id: it.id,
            inspection_id: it.inspection_id,
            vehicle_number: insp?.vehicle_number ?? "",
            driver_name: insp?.driver_name ?? null,
            created_at: insp?.created_at ?? "",
            category: it.category,
            label: it.label,
            status: it.status,
            note: it.note ?? "",
          };
        });

        // Keep only the most recent occurrence of the same issue (same
        // vehicle + category + item) across inspections -- an unresolved
        // item re-flagged on every subsequent inspection would otherwise
        // pile up as repeated entries instead of one current one.
        const latestByIssueKey = new Map<string, InspectionIssueItemRow>();
        for (const item of issueItems) {
          const key = `${item.vehicle_number}|${item.category}|${item.label}`;
          const existing = latestByIssueKey.get(key);
          if (!existing || item.created_at > existing.created_at) {
            latestByIssueKey.set(key, item);
          }
        }
        issueItems = Array.from(latestByIssueKey.values());
      }

      setFleetIssues({
        inspectionUnsafe: issueItems.filter((i) => i.status === "Unsafe"),
        inspectionFixSoon: issueItems.filter(
          (i) => i.status === "Needs Fixing Soon"
        ),
        inspectionAttention: issueItems.filter(
          (i) => i.status === "Needs Attention"
        ),
        maintenanceNow: vehicles.filter(
          (v) => String(v.status || "").toLowerCase() === "maintenance"
        ),
        serviceThisWeek: vehicles.filter(
          (v) =>
            !!v.service_booked_for &&
            v.service_booked_for >= weekStartIso &&
            v.service_booked_for <= weekEndIso
        ),
        cofThisWeek: vehicles.filter(
          (v) =>
            !!v.cof_booked_for &&
            v.cof_booked_for >= weekStartIso &&
            v.cof_booked_for <= weekEndIso
        ),
        tyreThisWeek: vehicles.filter(
          (v) =>
            !!v.tyre_rotation_booked_for &&
            v.tyre_rotation_booked_for >= weekStartIso &&
            v.tyre_rotation_booked_for <= weekEndIso
        ),
      });

      setRosterIssues({
        pendingTimeOff: (pendRes.data || []) as TimeOffRow[],
        approvedTimeOff: (apprRes.data || []) as TimeOffRow[],
        unavail: (unavailRes.data || []) as UnavailRow[],
        weekStartIso,
        weekEndIso,
      });

      setAccountApprovals(
        ((acctApprovalRes.data || []) as { account_id: string; account_name: string; active_to: string }[]).map((a) => ({
          account_id: a.account_id,
          account_name: a.account_name,
          active_to: a.active_to,
        }))
      );

      const permBookingRows = (permBookingRes.data || []) as {
        id: string;
        account_id: string;
        booking_name: string;
        expiry_date: string;
      }[];

      // Named lookup by the specific account_ids in play, rather than
      // pulling all ~4000 crm_accounts rows -- Supabase caps an unfiltered
      // select at 1000, which was silently dropping names for any account
      // outside that arbitrary first page.
      const bookingAccountIds = Array.from(new Set(permBookingRows.map((p) => p.account_id)));
      const accountNameMap = new Map<string, string>();
      // A permanent booking on an inactive client is moot, so those don't
      // count as expired/expiring either.
      const inactiveAccountIds = new Set<string>();
      if (bookingAccountIds.length > 0) {
        const { data: nameRows } = await supabase
          .from("crm_accounts")
          .select("account_id, account_name, is_active")
          .in("account_id", bookingAccountIds);
        ((nameRows || []) as { account_id: string; account_name: string; is_active: boolean | null }[]).forEach((a) => {
          accountNameMap.set(a.account_id, a.account_name);
          if (a.is_active === false) inactiveAccountIds.add(a.account_id);
        });
      }

      setPermBookingExpiries(
        permBookingRows
          .filter((p) => !inactiveAccountIds.has(p.account_id))
          .map((p) => ({
          id: p.id,
          account_id: p.account_id,
          account_name: accountNameMap.get(p.account_id) || p.account_id,
          booking_name: p.booking_name,
          expiry_date: p.expiry_date,
        }))
      );
    } catch (err) {
      console.error("Error loading action required items:", err);
      toast({
        title: "Action Required error",
        description: "Could not load action required items.",
        variant: "destructive",
      });
    } finally {
      setIssuesLoading(false);
    }
  };

  const reload = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const startIso = fmtIsoLocal(weekStart);
      const endIso = fmtIsoLocal(weekEnd);
      const driverSet = await loadActiveDrivers();
      const driverList = Array.from(driverSet);

      const { data: allData, error: allError } = await supabase
        .from("taxicaller_data")
        .select("driver_name, pu_date, Job_ID, fleet, Stage")
        .gte("pu_date", startIso)
        .lte("pu_date", endIso)
        .neq("fleet", EXCLUDED_FLEET)
        .order("driver_name", { ascending: true })
        .order("pu_date", { ascending: true });

      if (allError) throw allError;

      const { data: openData, error: openError } = await supabase
        .from("taxicaller_data")
        .select("driver_name, pu_date, Job_ID, fleet, Stage")
        .gte("pu_date", startIso)
        .lte("pu_date", endIso)
        .is("driver_completed", null)
        .neq("fleet", EXCLUDED_FLEET)
        .order("driver_name", { ascending: true })
        .order("pu_date", { ascending: true });

      if (openError) throw openError;

      const dcpOut: DcpRow[] = [];

      if (driverList.length > 0) {
        const results = await Promise.all(
          driverList.map(async (driverName) => {
            const { data, error } = await supabase.rpc(
              "get_dcp_for_driver_range",
              {
                p_driver_name: driverName,
                p_start: startIso,
                p_end: endIso,
              }
            );

            if (error) {
              console.error("DCP RPC error for", driverName, error);
              return [] as DcpRow[];
            }

            return (data ?? []).map((r: any) => ({
              driver_name: (r.driver_name ?? driverName) as string,
              reconciled: (r.reconciled ?? null) as boolean | null,
              driver_paid: (r.driver_paid ?? null) as boolean | null,
            })) as DcpRow[];
          })
        );

        for (const chunk of results) dcpOut.push(...chunk);
      }

      const filterFleet = (rows: NullJobRow[]) =>
        rows.filter(
          (r) =>
            (!r.fleet || r.fleet.toLowerCase() !== EXCLUDED_FLEET) &&
            !isCancelledOrNoShow(r.Stage)
        );

      setAllRows(filterFleet(allData ?? []));
      setOpenRows(filterFleet(openData ?? []));
      setDcpRows(dcpOut);
    } catch (e: any) {
      const msg = e?.message || "Failed to load data.";
      setErrorMsg(msg);
      toast({ title: "Load error", description: msg });
      setAllRows([]);
      setOpenRows([]);
      setDcpRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadChecksDue = async () => {
    try {
      setChecksLoading(true);
      const { rows } = await loadVehicleChecks(scopedVehicleNumbers);
      setVehicleChecks(rows);
    } catch (err) {
      console.error("Error loading inspection and camera checks:", err);
      setVehicleChecks([]);
    } finally {
      setChecksLoading(false);
    }
  };

  const reloadAll = async () => {
    // New Accounts / Account Updates are pure CRM data with no
    // driver/vehicle tie -- not fetched at all for a restricted user
    // rather than fetched and hidden.
    await Promise.all([
      reload(),
      loadActionRequired(),
      ...(scopedDriverNames ? [] : [loadPendingAccounts(), loadAccountFieldChanges()]),
      loadShiftFlags(),
      loadPendingIncidents(),
      loadChecksDue(),
    ]);
  };

  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart.getTime(), weekEnd.getTime()]);

  const buckets: DriverBucket[] = useMemo(() => {
    // Map every normalised active-driver name -> its canonical display name.
    // Job records are matched against this so that any difference in casing or
    // spacing between `drivers` and `taxicaller_data` no longer hides jobs.
    const normToCanonical = new Map<string, string>();
    for (const driverName of activeDrivers) {
      const norm = normalizeName(driverName);
      if (norm) normToCanonical.set(norm, driverName);
    }

    const totalByDriver = new Map<string, number>();
    for (const r of allRows) {
      const canonical = normToCanonical.get(normalizeName(r.driver_name));
      if (!canonical) continue;
      totalByDriver.set(canonical, (totalByDriver.get(canonical) || 0) + 1);
    }

    const dcpByDriver = new Map<string, number>();
    const reconciledByDriver = new Map<string, number>();
    const paidByDriver = new Map<string, number>();
    for (const r of dcpRows) {
      const canonical = normToCanonical.get(normalizeName(r.driver_name));
      if (!canonical) continue;
      dcpByDriver.set(canonical, (dcpByDriver.get(canonical) || 0) + 1);
      if (r.reconciled === true) {
        reconciledByDriver.set(
          canonical,
          (reconciledByDriver.get(canonical) || 0) + 1
        );
      }
      if (r.driver_paid === true) {
        paidByDriver.set(canonical, (paidByDriver.get(canonical) || 0) + 1);
      }
    }

    const openByDriver = new Map<
      string,
      Array<{ pu_date: string; Job_ID: string }>
    >();
    for (const r of openRows) {
      const canonical = normToCanonical.get(normalizeName(r.driver_name));
      if (!canonical) continue;
      if (!openByDriver.has(canonical)) openByDriver.set(canonical, []);
      openByDriver
        .get(canonical)!
        .push({ pu_date: r.pu_date || "", Job_ID: r.Job_ID || "" });
    }

    return Array.from(activeDrivers)
      .sort()
      .map((driverName) => ({
        driver_name: driverName,
        totalJobs: totalByDriver.get(driverName) || 0,
        openJobs: openByDriver.get(driverName)?.length || 0,
        dcpJobs: dcpByDriver.get(driverName) || 0,
        reconciledJobs: reconciledByDriver.get(driverName) || 0,
        paidJobs: paidByDriver.get(driverName) || 0,
        openRows: openByDriver.get(driverName) || [],
      }));
  }, [allRows, openRows, dcpRows, activeDrivers]);

  const totalOpen = useMemo(() => {
    const activeNorms = new Set(
      Array.from(activeDrivers).map((d) => normalizeName(d))
    );
    return openRows.filter((r) => activeNorms.has(normalizeName(r.driver_name)))
      .length;
  }, [openRows, activeDrivers]);

  const openDriverBuckets = useMemo(
    () => buckets.filter((b) => b.openJobs > 0),
    [buckets]
  );

  const closedNotReconciledBuckets = useMemo(
    () => buckets.filter(isClosedNotReconciled),
    [buckets]
  );

  const fullyReconciledBuckets = useMemo(
    () => buckets.filter(isFullyReconciled),
    [buckets]
  );

  const noJobsBuckets = useMemo(
    () => buckets.filter(hasNoJobsThisWeek),
    [buckets]
  );

  const driversWithDcpJobs = useMemo(
    () => buckets.filter((b) => b.dcpJobs > 0).length,
    [buckets]
  );

  const checkCounts = useMemo(() => summarizeChecks(vehicleChecks), [vehicleChecks]);

  // Overdue (including never-checked) and due-soon checks, most urgent first -- one list pair per kind of check.
  const checkItems = useMemo(() => {
    type Item = { key: string; label: string; urgency: number };
    const admin = { overdue: [] as Item[], soon: [] as Item[] };
    const camera = { overdue: [] as Item[], soon: [] as Item[] };
    for (const r of vehicleChecks) {
      const name = r.registration ? `${r.vehicle_number} (${r.registration})` : r.vehicle_number;
      const add = (bucket: typeof admin, kind: string, s: CheckStatus | null, partDone: number | null = null) => {
        if (!s || s.state === "ok") return;
        const progress = partDone != null ? ` · ${partDone}% done` : "";
        const item = { key: `${kind}-${r.vehicle_number}`, label: `${name} — ${statusLabel(s)}${progress}`, urgency: checkUrgency(s) };
        (s.state === "due_soon" ? bucket.soon : bucket.overdue).push(item);
      };
      add(admin, "admin", r.admin, r.adminProgress);
      add(camera, "camera", r.camera);
    }
    const byUrgency = (a: { urgency: number }, b: { urgency: number }) => a.urgency - b.urgency;
    for (const bucket of [admin, camera]) {
      bucket.overdue.sort(byUrgency);
      bucket.soon.sort(byUrgency);
    }
    return { admin, camera };
  }, [vehicleChecks]);

  const fleetIssueCount = useMemo(() => {
    const checks = checkCounts.total;
    if (!fleetIssues) return checks;
    return (
      fleetIssues.inspectionUnsafe.length +
      fleetIssues.inspectionFixSoon.length +
      fleetIssues.inspectionAttention.length +
      fleetIssues.maintenanceNow.length +
      fleetIssues.serviceThisWeek.length +
      fleetIssues.cofThisWeek.length +
      fleetIssues.tyreThisWeek.length +
      checks
    );
  }, [fleetIssues, checkCounts]);

  const rosterIssueCount = useMemo(() => {
    if (!rosterIssues) return 0;
    return (
      rosterIssues.pendingTimeOff.length +
      rosterIssues.approvedTimeOff.length +
      rosterIssues.unavail.length
    );
  }, [rosterIssues]);

  const fleetInspectionIssueCount = useMemo(() => {
    if (!fleetIssues) return 0;
    return (
      fleetIssues.inspectionUnsafe.length +
      fleetIssues.inspectionFixSoon.length +
      fleetIssues.inspectionAttention.length
    );
  }, [fleetIssues]);

  const fleetServiceIssueCount = useMemo(() => {
    if (!fleetIssues) return 0;
    return (
      fleetIssues.maintenanceNow.length +
      fleetIssues.serviceThisWeek.length +
      fleetIssues.cofThisWeek.length +
      fleetIssues.tyreThisWeek.length
    );
  }, [fleetIssues]);

  const accountApprovalBuckets = useMemo(() => {
    const todayIso = fmtIsoLocal(new Date());
    const expired = accountApprovals.filter((a) => daysRemaining(a.active_to, todayIso) < 0);
    const soon = accountApprovals.filter((a) => {
      const d = daysRemaining(a.active_to, todayIso);
      return d >= 0 && d <= 7;
    });
    const upcoming = accountApprovals.filter((a) => {
      const d = daysRemaining(a.active_to, todayIso);
      return d > 7 && d <= 30;
    });
    return { expired, soon, upcoming, todayIso };
  }, [accountApprovals]);

  const permBookingBuckets = useMemo(() => {
    const todayIso = fmtIsoLocal(new Date());
    const expired = permBookingExpiries.filter((p) => daysRemaining(p.expiry_date, todayIso) < 0);
    const soon = permBookingExpiries.filter((p) => {
      const d = daysRemaining(p.expiry_date, todayIso);
      return d >= 0 && d <= 7;
    });
    const upcoming = permBookingExpiries.filter((p) => {
      const d = daysRemaining(p.expiry_date, todayIso);
      return d > 7 && d <= 30;
    });
    return { expired, soon, upcoming, todayIso };
  }, [permBookingExpiries]);

  const actionRequiredCount =
    fleetIssueCount + rosterIssueCount + accountApprovals.length + permBookingExpiries.length;

  const toggleExpanded = (driverName: string) => {
    setExpandedDrivers((prev) => {
      const next = new Set(prev);
      if (next.has(driverName)) next.delete(driverName);
      else next.add(driverName);
      return next;
    });
  };

  const sideTabs = [
    {
      value: "action" as const,
      label: "Action Required",
      description: "Fleet, compliance, maintenance and roster alerts",
      icon: ShieldAlert,
      count: actionRequiredCount,
    },
    {
      value: "open" as const,
      label: "Open Jobs",
      description: "Drivers with jobs still open",
      icon: Clock3,
      count: openDriverBuckets.length,
    },
    {
      value: "closedNotReconciled" as const,
      label: "Closed Not Reconciled",
      description: "Closed driver weeks needing payment reconciliation",
      icon: FileWarning,
      count: closedNotReconciledBuckets.length,
    },
    {
      value: "fullyReconciled" as const,
      label: "Fully Reconciled",
      description: "Driver weeks completed and balanced",
      icon: CircleCheckBig,
      count: fullyReconciledBuckets.length,
    },
    {
      value: "noJobs" as const,
      label: "No Jobs This Week",
      description: "Active report drivers with no jobs this week",
      icon: CalendarX2,
      count: noJobsBuckets.length,
    },
    {
      value: "shiftIssues" as const,
      label: "Shift Issues",
      description: "Drivers with too many open shifts, a shift left open too long, or a missing starting odometer",
      icon: AlertTriangle,
      count: shiftFlags.length,
    },
    {
      value: "incidentReports" as const,
      label: "Incident Reports",
      description: "Driver-submitted incidents not yet reviewed by an admin",
      icon: Siren,
      count: pendingIncidents.length,
    },
    // New Accounts / Account Updates are pure CRM data with no
    // driver/vehicle tie -- hidden entirely for a restricted user.
    ...(scopedDriverNames
      ? []
      : [
          {
            value: "newAccounts" as const,
            label: "New Accounts",
            description: "TaxiCaller account codes not yet in the CRM",
            icon: Building2,
            count: pendingAccounts.length,
          },
          {
            value: "accountUpdates" as const,
            label: "Account Updates",
            description: "Account name, phone or status differs from TaxiCaller",
            icon: AlertCircle,
            count: accountFieldChanges.length,
          },
        ]),
  ];

  const renderDriverCards = (
    list: DriverBucket[],
    emptyTitle: string,
    emptyDescription: string
  ) => {
    if (list.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-border bg-background/30 p-8 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-3" />
          <div className="text-lg font-semibold text-foreground">{emptyTitle}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {emptyDescription}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((bucket) => (
          <DriverStatusCard
            key={bucket.driver_name}
            bucket={bucket}
            expanded={expandedDrivers.has(bucket.driver_name)}
            onToggle={() => toggleExpanded(bucket.driver_name)}
          />
        ))}
      </div>
    );
  };

  const renderActionRequired = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ActionPanel
          title="Fleet Issues"
          icon={<Car className="w-5 h-5 text-blue-300" />}
          count={fleetInspectionIssueCount}
        >
          {issuesLoading ? (
            <LoadingMiniState message="Loading fleet issues..." />
          ) : !fleetIssues || fleetInspectionIssueCount === 0 ? (
            <EmptyMiniState message="No fleet inspection issues right now." />
          ) : (
            <div className="space-y-3">
              {fleetIssues.inspectionUnsafe.length > 0 && (
                <GroupedIssueList
                  tone="red"
                  title={`${fleetIssues.inspectionUnsafe.length} unsafe inspection${
                    fleetIssues.inspectionUnsafe.length !== 1 ? "s" : ""
                  }`}
                  groups={groupIssueItems(fleetIssues.inspectionUnsafe)}
                />
              )}

              {fleetIssues.inspectionFixSoon.length > 0 && (
                <GroupedIssueList
                  tone="orange"
                  title={`${fleetIssues.inspectionFixSoon.length} needs fixing soon`}
                  groups={groupIssueItems(fleetIssues.inspectionFixSoon)}
                />
              )}

              {fleetIssues.inspectionAttention.length > 0 && (
                <GroupedIssueList
                  tone="amber"
                  title={`${fleetIssues.inspectionAttention.length} needs attention`}
                  groups={groupIssueItems(fleetIssues.inspectionAttention)}
                />
              )}
            </div>
          )}

          {/* Always shown, independent of the inspection-issue branches above --
              driver-submitted incidents awaiting admin review (same queue as the
              Incident Reports tab). Always visible with its own header per Janelle,
              rather than folding into "no fleet issues" when there aren't any. */}
          <div className="pt-3 mt-3 border-t border-border/60">
            <div className="text-xs font-semibold text-foreground mb-1.5">Incidents</div>
            {pendingIncidentsLoading ? (
              <LoadingMiniState message="Loading incidents..." />
            ) : pendingIncidents.length === 0 ? (
              <div className="rounded-xl border border-green-800/40 bg-green-950/20 p-3 text-sm text-green-300 flex items-center gap-2">
                <Smile className="w-4 h-4" />
                0 incidents
              </div>
            ) : (
              <IssueList
                tone="red"
                title={`${pendingIncidents.length} unreviewed incident${pendingIncidents.length !== 1 ? "s" : ""}`}
                items={pendingIncidents.map((inc) => ({
                  key: inc.id,
                  label: `${inc.vehicle_number || "—"} — ${formatDate(inc.incident_date)} — ${inc.driver_name}`,
                }))}
              />
            )}
          </div>
        </ActionPanel>

        <ActionPanel
          title="Services, COFs & Maintenance"
          icon={<Wrench className="w-5 h-5 text-purple-300" />}
          count={fleetServiceIssueCount}
        >
          {issuesLoading ? (
            <LoadingMiniState message="Loading service and maintenance alerts..." />
          ) : !fleetIssues || fleetServiceIssueCount === 0 ? (
            <EmptyMiniState message="No service, COF or maintenance alerts right now." />
          ) : (
            <div className="space-y-3">
              {fleetIssues.maintenanceNow.length > 0 && (
                <IssueList
                  tone="red"
                  title={`${fleetIssues.maintenanceNow.length} currently in maintenance`}
                  items={fleetIssues.maintenanceNow.map((v) => ({
                    key: `mnt-${v.vehicle_number}`,
                    label: v.vehicle_number,
                  }))}
                />
              )}

              {(fleetIssues.serviceThisWeek.length > 0 ||
                fleetIssues.cofThisWeek.length > 0 ||
                fleetIssues.tyreThisWeek.length > 0) && (
                <GroupedIssueList
                  tone="blue"
                  title={`${
                    fleetIssues.serviceThisWeek.length +
                    fleetIssues.cofThisWeek.length +
                    fleetIssues.tyreThisWeek.length
                  } booking${
                    fleetIssues.serviceThisWeek.length +
                      fleetIssues.cofThisWeek.length +
                      fleetIssues.tyreThisWeek.length !==
                    1
                      ? "s"
                      : ""
                  } this week`}
                  groups={groupServiceBookings(
                    fleetIssues.serviceThisWeek,
                    fleetIssues.cofThisWeek,
                    fleetIssues.tyreThisWeek
                  )}
                />
              )}
            </div>
          )}
        </ActionPanel>

        <ActionPanel
          title="Admin Inspections"
          icon={<ClipboardCheck className="w-5 h-5 text-cyan-300" />}
          count={checkCounts.adminOverdue + checkCounts.adminDueSoon}
        >
          {checksLoading ? (
            <LoadingMiniState message="Loading admin inspections..." />
          ) : checkCounts.adminOverdue + checkCounts.adminDueSoon === 0 ? (
            <EmptyMiniState message="No admin inspections due." />
          ) : (
            <div className="space-y-3">
              {checkItems.admin.overdue.length > 0 && (
                <IssueList tone="red" title={`${checkItems.admin.overdue.length} overdue or never checked`} items={checkItems.admin.overdue} />
              )}
              {checkItems.admin.soon.length > 0 && (
                <IssueList tone="amber" title={`${checkItems.admin.soon.length} due soon`} items={checkItems.admin.soon} />
              )}
              <div className="text-[11px] text-muted-foreground pt-1">
                Record these from Fleet with the clipboard button.
              </div>
            </div>
          )}
        </ActionPanel>

        <ActionPanel
          title="Camera Checks"
          icon={<Camera className="w-5 h-5 text-cyan-300" />}
          count={checkCounts.cameraOverdue + checkCounts.cameraDueSoon}
        >
          {checksLoading ? (
            <LoadingMiniState message="Loading camera checks..." />
          ) : checkCounts.cameraOverdue + checkCounts.cameraDueSoon === 0 ? (
            <EmptyMiniState message="No camera checks due." />
          ) : (
            <div className="space-y-3">
              {checkItems.camera.overdue.length > 0 && (
                <IssueList tone="red" title={`${checkItems.camera.overdue.length} overdue or never checked`} items={checkItems.camera.overdue} />
              )}
              {checkItems.camera.soon.length > 0 && (
                <IssueList tone="amber" title={`${checkItems.camera.soon.length} due soon`} items={checkItems.camera.soon} />
              )}
              <div className="text-[11px] text-muted-foreground pt-1">
                Record these from Fleet with the camera button.
              </div>
            </div>
          )}
        </ActionPanel>

        <ActionPanel
          title="Roster Alerts"
          icon={<CalendarDays className="w-5 h-5 text-amber-300" />}
          count={rosterIssueCount}
        >
          {issuesLoading ? (
            <LoadingMiniState message="Loading roster alerts..." />
          ) : !rosterIssues || rosterIssueCount === 0 ? (
            <EmptyMiniState message="No roster alerts right now." />
          ) : (
            <div className="space-y-3">
              {(rosterIssues.pendingTimeOff.length > 0 ||
                rosterIssues.approvedTimeOff.length > 0 ||
                rosterIssues.unavail.length > 0) && (
                <GroupedIssueList
                  tone="amber"
                  title={`${
                    rosterIssues.pendingTimeOff.length +
                    rosterIssues.approvedTimeOff.length +
                    rosterIssues.unavail.length
                  } roster alert${
                    rosterIssues.pendingTimeOff.length +
                      rosterIssues.approvedTimeOff.length +
                      rosterIssues.unavail.length !==
                    1
                      ? "s"
                      : ""
                  }`}
                  groups={groupRosterAlerts(
                    rosterIssues.pendingTimeOff,
                    rosterIssues.approvedTimeOff,
                    rosterIssues.unavail
                  )}
                />
              )}

              <div className="text-[11px] text-muted-foreground pt-1">
                Week of {formatDate(rosterIssues.weekStartIso)} – {formatDate(rosterIssues.weekEndIso)}
              </div>
            </div>
          )}
        </ActionPanel>

        <ActionPanel
          title="Account Approvals"
          icon={<Building2 className="w-5 h-5 text-emerald-300" />}
          count={accountApprovals.length}
        >
          {issuesLoading ? (
            <LoadingMiniState message="Loading account approvals..." />
          ) : accountApprovals.length === 0 ? (
            <EmptyMiniState message="No account approvals expired or expiring within 30 days." />
          ) : (
            <div className="space-y-3">
              {accountApprovalBuckets.expired.length > 0 && (
                <IssueList
                  tone="red"
                  title={`${accountApprovalBuckets.expired.length} expired`}
                  onSelect={openCrmAccount}
                  items={accountApprovalBuckets.expired.map((a) => ({
                    key: a.account_id,
                    target: a.account_id,
                    label: `${a.account_name} (${a.account_id}) — ${formatDate(a.active_to)}, ${expiryDaysLabel(
                      daysRemaining(a.active_to, accountApprovalBuckets.todayIso)
                    )}`,
                  }))}
                />
              )}
              {accountApprovalBuckets.soon.length > 0 && (
                <IssueList
                  tone="orange"
                  title={`${accountApprovalBuckets.soon.length} expiring within 7 days`}
                  onSelect={openCrmAccount}
                  items={accountApprovalBuckets.soon.map((a) => ({
                    key: a.account_id,
                    target: a.account_id,
                    label: `${a.account_name} (${a.account_id}) — ${formatDate(a.active_to)}, ${expiryDaysLabel(
                      daysRemaining(a.active_to, accountApprovalBuckets.todayIso)
                    )}`,
                  }))}
                />
              )}
              {accountApprovalBuckets.upcoming.length > 0 && (
                <IssueList
                  tone="amber"
                  title={`${accountApprovalBuckets.upcoming.length} expiring within 30 days`}
                  onSelect={openCrmAccount}
                  items={accountApprovalBuckets.upcoming.map((a) => ({
                    key: a.account_id,
                    target: a.account_id,
                    label: `${a.account_name} (${a.account_id}) — ${formatDate(a.active_to)}, ${expiryDaysLabel(
                      daysRemaining(a.active_to, accountApprovalBuckets.todayIso)
                    )}`,
                  }))}
                />
              )}
            </div>
          )}
        </ActionPanel>

        <ActionPanel
          title="Permanent Bookings"
          icon={<CalendarDays className="w-5 h-5 text-cyan-300" />}
          count={permBookingExpiries.length}
        >
          {issuesLoading ? (
            <LoadingMiniState message="Loading permanent bookings..." />
          ) : permBookingExpiries.length === 0 ? (
            <EmptyMiniState message="No permanent bookings expired or expiring within 30 days." />
          ) : (
            <div className="space-y-3">
              {permBookingBuckets.expired.length > 0 && (
                <IssueList
                  tone="red"
                  title={`${permBookingBuckets.expired.length} expired`}
                  onSelect={openCrmAccount}
                  items={permBookingBuckets.expired.map((p) => ({
                    key: p.id,
                    target: p.account_id,
                    label: `${p.account_name} (${p.account_id}) — ${p.booking_name} — ${formatDate(
                      p.expiry_date
                    )}, ${expiryDaysLabel(daysRemaining(p.expiry_date, permBookingBuckets.todayIso))}`,
                  }))}
                />
              )}
              {permBookingBuckets.soon.length > 0 && (
                <IssueList
                  tone="orange"
                  title={`${permBookingBuckets.soon.length} expiring within 7 days`}
                  onSelect={openCrmAccount}
                  items={permBookingBuckets.soon.map((p) => ({
                    key: p.id,
                    target: p.account_id,
                    label: `${p.account_name} (${p.account_id}) — ${p.booking_name} — ${formatDate(
                      p.expiry_date
                    )}, ${expiryDaysLabel(daysRemaining(p.expiry_date, permBookingBuckets.todayIso))}`,
                  }))}
                />
              )}
              {permBookingBuckets.upcoming.length > 0 && (
                <IssueList
                  tone="amber"
                  title={`${permBookingBuckets.upcoming.length} expiring within 30 days`}
                  onSelect={openCrmAccount}
                  items={permBookingBuckets.upcoming.map((p) => ({
                    key: p.id,
                    target: p.account_id,
                    label: `${p.account_name} (${p.account_id}) — ${p.booking_name} — ${formatDate(
                      p.expiry_date
                    )}, ${expiryDaysLabel(daysRemaining(p.expiry_date, permBookingBuckets.todayIso))}`,
                  }))}
                />
              )}
            </div>
          )}
        </ActionPanel>
      </div>
    </div>
  );

  const renderShiftIssues = () => {
    const refreshButton = (
      <Button
        variant="outline"
        size="sm"
        onClick={handleRefreshShiftFlags}
        disabled={refreshingShiftFlags || shiftFlagsLoading}
      >
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshingShiftFlags ? "animate-spin" : ""}`} />
        {refreshingShiftFlags ? "Checking..." : "Refresh"}
      </Button>
    );

    if (shiftFlagsLoading) {
      return <LoadingMiniState message="Loading shift issues..." />;
    }

    if (shiftFlags.length === 0) {
      return (
        <div className="space-y-3">
          <div className="flex justify-end">{refreshButton}</div>
          <div className="rounded-2xl border border-dashed border-border bg-background/30 p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-3" />
            <div className="text-lg font-semibold text-foreground">No shift issues</div>
            <div className="text-sm text-muted-foreground mt-1">
              No drivers currently have too many open shifts, a shift left open too long, or a missing starting
              odometer. Auto-checked every 15 minutes.
            </div>
          </div>
        </div>
      );
    }

    const groups = Object.keys(SHIFT_FLAG_LABELS)
      .map((type) => ({
        type,
        label: SHIFT_FLAG_LABELS[type],
        rows: shiftFlags.filter((f) => f.flag_type === type),
      }))
      .filter((g) => g.rows.length > 0);

    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground max-w-2xl">
            Auto-flagged every 15 minutes from open shifts -- unclosed and un-odometered shifts are a big part of
            what let vehicles run over their RUC limit. This is a nudge, not an enforced limit.
          </p>
          {refreshButton}
        </div>
        {groups.map((g) => (
          <div key={g.type} className="space-y-2">
            <div className="text-sm font-semibold text-foreground">
              {g.label} ({g.rows.length})
            </div>
            {g.rows.map((f) => (
              <div
                key={f.id}
                className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-border bg-secondary/20 p-3"
              >
                <div>
                  <div className="font-semibold text-foreground">
                    {f.driver_name}
                    {f.vehicle_number ? ` — ${f.vehicle_number}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{f.detail}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const humanize = (s: string | null) => (s ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "—");

  const renderIncidentReports = () => {
    if (pendingIncidentsLoading) {
      return <LoadingMiniState message="Loading incident reports..." />;
    }
    if (pendingIncidents.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-border bg-background/30 p-8 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-3" />
          <div className="text-lg font-semibold text-foreground">No unreviewed incidents</div>
          <div className="text-sm text-muted-foreground mt-1">
            Every driver-submitted incident report has been reviewed.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Submitted by a driver, not yet reviewed by an admin. Disappears from here once you mark it reviewed in
          Insurance -&gt; Incidents.
        </p>
        {pendingIncidents.map((inc) => (
          <div
            key={inc.id}
            className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-border bg-secondary/20 p-3"
          >
            <div>
              <div className="font-semibold text-foreground">
                {inc.driver_name}
                {inc.vehicle_number ? ` — ${inc.vehicle_number}` : ""}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {humanize(inc.incident_category)}
                {inc.incident_date ? ` • ${formatDate(inc.incident_date)}` : ""}
              </div>
              {inc.narrative && (
                <div className="text-xs text-muted-foreground/80 mt-1 max-w-xl truncate">{inc.narrative}</div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateAdmin("insurance", undefined, ["incidents", inc.id])}
            >
              Review
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderNewAccounts = () => {
    const refreshButton = (
      <Button variant="outline" size="sm" onClick={handleRunAccountSync} disabled={checkingAccountUpdates || pendingAccountsLoading}>
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${checkingAccountUpdates ? "animate-spin" : ""}`} />
        {checkingAccountUpdates ? "Checking..." : "Refresh"}
      </Button>
    );

    if (pendingAccountsLoading) {
      return <LoadingMiniState message="Loading new accounts..." />;
    }
    if (pendingAccounts.length === 0) {
      return (
        <div className="space-y-3">
          <div className="flex justify-end">{refreshButton}</div>
          <div className="rounded-2xl border border-dashed border-border bg-background/30 p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-3" />
            <div className="text-lg font-semibold text-foreground">No new accounts</div>
            <div className="text-sm text-muted-foreground mt-1">
              Every TaxiCaller account code seen so far already has a matching CRM account.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground max-w-2xl">
            These account codes exist in TaxiCaller but don't have a matching CRM account yet. Create Account opens
            the CRM form pre-filled with what TaxiCaller has (name, phone, billing email, Billed/Direct, required
            fields) so you just check and complete it; Dismiss ignores this code
            permanently (e.g. a typo or duplicate). New codes show up here automatically as soon as a job comes in for
            them -- Refresh checks TaxiCaller's full account list live, so a brand-new account shows up even before
            its first booking.
          </p>
          {refreshButton}
        </div>
        {pendingAccounts.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-border bg-secondary/20 p-3"
          >
            <div>
              <div className="font-semibold text-foreground">Account {p.account_id}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {p.sample_job_id
                  ? p.sample_passenger_name
                    ? `First seen on a booking for ${p.sample_passenger_name} (Job ${p.sample_job_id})`
                    : `First seen on a booking (Job ${p.sample_job_id})`
                  : p.sample_passenger_name
                  ? `Found in TaxiCaller's account list — Customer: ${p.sample_passenger_name}`
                  : "Found in TaxiCaller's account list"}{" "}
                — {formatDate(p.first_seen_at)}
              </div>
              {taxiCallerSummary(p.taxicaller_details) && (
                <div className="text-xs text-muted-foreground/80 mt-0.5">{taxiCallerSummary(p.taxicaller_details)}</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => handleCreateAccount(p)}>
                Create Account
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDismissAccount(p)}>
                <X className="w-3.5 h-3.5 mr-1" />
                Dismiss
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAccountUpdates = () => {
    const refreshButton = (
      <Button variant="outline" size="sm" onClick={handleRunAccountSync} disabled={checkingAccountUpdates || accountFieldChangesLoading}>
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${checkingAccountUpdates ? "animate-spin" : ""}`} />
        {checkingAccountUpdates ? "Checking..." : "Refresh"}
      </Button>
    );

    if (accountFieldChangesLoading) {
      return <LoadingMiniState message="Loading account updates..." />;
    }
    if (accountFieldChanges.length === 0) {
      return (
        <div className="space-y-3">
          <div className="flex justify-end">{refreshButton}</div>
          <div className="rounded-2xl border border-dashed border-border bg-background/30 p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-3" />
            <div className="text-lg font-semibold text-foreground">No account updates</div>
            <div className="text-sm text-muted-foreground mt-1">
              Every CRM account's name, phone and active status match TaxiCaller.
            </div>
          </div>
        </div>
      );
    }

    const byAccount = new Map<string, AccountFieldChange[]>();
    for (const c of accountFieldChanges) {
      const list = byAccount.get(c.account_id) ?? [];
      list.push(c);
      byAccount.set(c.account_id, list);
    }

    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground max-w-2xl">
            TaxiCaller's account data differs from the CRM for these accounts (passenger details on individual jobs
            aren't checked here — only the account itself). Approve Update pulls TaxiCaller's value into the CRM; Keep
            Original leaves the CRM as-is and stops flagging this specific difference. This list normally only
            refreshes once a day -- use Refresh to re-check TaxiCaller right now.
          </p>
          {refreshButton}
        </div>
        {Array.from(byAccount.entries()).map(([accountId, changes]) => (
          <div key={accountId} className="rounded-xl border border-border bg-secondary/20 p-3 space-y-2">
            <div className="font-semibold text-foreground">Account {accountId}</div>
            {changes.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-border/60 bg-background/30 p-2.5"
              >
                <div className="text-sm">
                  <span className="font-medium text-foreground">{ACCOUNT_FIELD_LABELS[c.field_name] ?? c.field_name}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    — CRM: <span className="text-foreground">{c.dms_value ?? "(blank)"}</span>, TaxiCaller:{" "}
                    <span className="text-foreground">{c.taxicaller_value ?? "(blank)"}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => handleApproveAccountUpdate(c)}>
                    Approve Update
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleKeepOriginalAccountValue(c)}>
                    <X className="w-3.5 h-3.5 mr-1" />
                    Keep Original
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-xl bg-card border border-border overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-900/25 via-background/40 to-background/20 border-b border-yellow-700/20 px-5 py-4">
          <div className="flex flex-col xl:flex-row xl:items-end gap-4">
            <div className="flex-1">
              <CardTitle className="text-2xl text-primary flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6" />
                Closed Check
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Action queue, open jobs and driver status for the selected week.
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-end gap-3">
              <div>
                <label className="block text-sm mb-1 text-muted-foreground">
                  Week of
                </label>
                <Input
                  type="date"
                  value={fmtIsoLocal(weekStart)}
                  onChange={(e) => handleDatePick(e.target.value)}
                  className="dark-input w-[170px]"
                />
              </div>

              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={prevWeek} className="border-border">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={nextWeek} className="border-border">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button onClick={reloadAll} className="golden-button">
                  Reload
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Badge variant="secondary">
              Week: {formatDate(weekStart)} – {formatDate(weekEnd)}
            </Badge>
            <Badge className="bg-red-700 text-white">Open jobs: {totalOpen}</Badge>
            <Badge className="bg-orange-700 text-white">
              Not reconciled: {closedNotReconciledBuckets.length}
            </Badge>
            <Badge className="bg-green-700 text-white">
              Fully reconciled: {fullyReconciledBuckets.length} / {driversWithDcpJobs}
            </Badge>
          </div>

          {(loading || issuesLoading) && (
            <div className="mt-4 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading Closed Check workspace…
            </div>
          )}

          {errorMsg && <div className="mt-4 text-red-400">{errorMsg}</div>}
        </div>

        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
            <SummaryTile
              title="Action Required"
              value={actionRequiredCount}
              helper="Issues, open jobs and reconciliation exceptions"
              tone="red"
            />
            <SummaryTile
              title="Open Jobs"
              value={totalOpen}
              helper={`${openDriverBuckets.length} affected driver${
                openDriverBuckets.length !== 1 ? "s" : ""
              }`}
              tone="yellow"
            />
            <SummaryTile
              title="Closed Not Reconciled"
              value={closedNotReconciledBuckets.length}
              helper="Driver weeks still needing review"
              tone="orange"
            />
            <SummaryTile
              title="Fully Reconciled"
              value={`${fullyReconciledBuckets.length} / ${driversWithDcpJobs}`}
              helper="DCP jobs matched as reconciled"
              tone="green"
            />
            <SummaryTile
              title="New Accounts"
              value={pendingAccounts.length}
              helper="TaxiCaller codes awaiting CRM review"
              tone="blue"
            />
            <SummaryTile
              title="Account Updates"
              value={accountFieldChanges.length}
              helper="Account name/phone/status differs from TaxiCaller"
              tone="orange"
            />
          </div>

          <SettingsSidebarLayout
            tabs={sideTabs}
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as ClosedCheckTab)}
          >
              {activeTab === "action" && renderActionRequired()}
              {activeTab === "open" &&
                renderDriverCards(
                  openDriverBuckets,
                  "No open jobs",
                  "All active report drivers have their jobs closed for this week."
                )}
              {activeTab === "closedNotReconciled" &&
                renderDriverCards(
                  closedNotReconciledBuckets,
                  "Nothing waiting for reconciliation",
                  "No closed driver weeks are currently sitting unreconciled."
                )}
              {activeTab === "fullyReconciled" &&
                renderDriverCards(
                  fullyReconciledBuckets,
                  "No fully reconciled drivers yet",
                  "Once DCP jobs match as reconciled, they will appear here."
                )}
              {activeTab === "noJobs" &&
                renderDriverCards(
                  noJobsBuckets,
                  "Everyone has jobs this week",
                  "No active report drivers are sitting at zero jobs for this week."
                )}
              {activeTab === "shiftIssues" && renderShiftIssues()}
              {activeTab === "incidentReports" && renderIncidentReports()}
              {activeTab === "newAccounts" && !scopedDriverNames && renderNewAccounts()}
              {activeTab === "accountUpdates" && !scopedDriverNames && renderAccountUpdates()}
          </SettingsSidebarLayout>
        </CardContent>
      </Card>

      <CRMAccountForm
        open={!!formAccount}
        onOpenChange={(open) => {
          if (!open) setFormAccount(null);
        }}
        account={formAccount}
        onSaved={() => setFormAccount(null)}
      />
    </div>
  );
};

const SummaryTile: React.FC<{
  title: string;
  value: string | number;
  helper: string;
  tone: "red" | "yellow" | "orange" | "green" | "blue";
}> = ({ title, value, helper, tone }) => {
  const toneClass =
    tone === "red"
      ? "text-red-300 border-red-700/40 bg-red-950/25"
      : tone === "orange"
      ? "text-orange-300 border-orange-700/40 bg-orange-950/25"
      : tone === "green"
      ? "text-green-300 border-green-700/40 bg-green-950/25"
      : tone === "blue"
      ? "text-blue-300 border-blue-700/40 bg-blue-950/25"
      : "text-yellow-300 border-yellow-700/40 bg-yellow-950/25";

  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <div className="text-sm font-medium opacity-90">{title}</div>
      <div className="text-4xl font-bold mt-2">{value}</div>
      <div className="text-xs text-muted-foreground mt-2">{helper}</div>
    </div>
  );
};

const DriverStatusCard: React.FC<{
  bucket: DriverBucket;
  expanded: boolean;
  onToggle: () => void;
}> = ({ bucket, expanded, onToggle }) => {
  const fullyReconciled = isFullyReconciled(bucket);
  const closedNotReconciled = isClosedNotReconciled(bucket);
  const noJobs = hasNoJobsThisWeek(bucket);
  const hasOpenJobs = bucket.openJobs > 0;
  const hasUnreconciled = bucket.dcpJobs > 0 && bucket.reconciledJobs < bucket.dcpJobs;
  const closedAndPaid = isClosedAndPaid(bucket);

  let tileBg = "bg-card border-border";
  let statusLabel = "No Jobs";
  let badgeClass = "bg-gray-700 text-white";

  if (hasOpenJobs) {
    tileBg = "bg-red-950/50 border-red-800/60";
    statusLabel = "Open Jobs";
    badgeClass = "bg-red-700 text-white";
  } else if (fullyReconciled) {
    tileBg = "bg-green-950/50 border-green-800/60";
    statusLabel = "Reconciled";
    badgeClass = "bg-green-700 text-white";
  } else if (closedNotReconciled) {
    tileBg = "bg-orange-950/50 border-orange-700/60";
    statusLabel = "Review";
    badgeClass = "bg-orange-700 text-white";
  } else if (noJobs) {
    tileBg = "bg-slate-950/50 border-slate-700/60";
    statusLabel = "No Jobs";
    badgeClass = "bg-slate-700 text-white";
  }

  // Closed AND paid out needs no further action at all -- grey it out so it
  // visually recedes below anything still needing attention, and flag it
  // with a Paid badge rather than folding it into the Reconciled/Review
  // labels above (paid is a separate fact from reconciled).
  if (closedAndPaid) {
    tileBg = "bg-neutral-900/60 border-neutral-700/50 opacity-60";
  }

  return (
    <Card className={`shadow-md border transition-opacity ${tileBg}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-sm font-semibold truncate">
            {bucket.driver_name}
          </CardTitle>
          <div className="flex items-center gap-1.5 shrink-0">
            {closedAndPaid && (
              <Badge className="text-xs px-2 py-0.5 bg-neutral-600 text-white flex items-center gap-1">
                <BadgeDollarSign className="w-3 h-3" />
                Paid
              </Badge>
            )}
            <Badge className={`text-xs px-2 py-0.5 ${badgeClass}`}>
              {statusLabel}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 pt-1 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <MiniMetric label="Total" value={bucket.totalJobs} />
          <MiniMetric label="Open" value={bucket.openJobs} tone={hasOpenJobs ? "red" : "green"} />
          <MiniMetric
            label="Recon"
            value={`${bucket.reconciledJobs}/${bucket.dcpJobs}`}
            tone={fullyReconciled ? "green" : hasUnreconciled ? "red" : "muted"}
          />
        </div>

        {bucket.dcpJobs > 0 && (
          <div
            className={`flex items-center gap-2 text-xs p-2 rounded-lg ${
              fullyReconciled ? "bg-green-900/30" : "bg-yellow-900/30"
            }`}
          >
            {fullyReconciled ? (
              <CheckCircle2 className="w-4 h-4 text-green-400" />
            ) : (
              <AlertCircle className="w-4 h-4 text-yellow-400" />
            )}
            <span>
              DCP: <span className="font-semibold">{bucket.reconciledJobs}</span> /{" "}
              <span className="font-semibold">{bucket.dcpJobs}</span> reconciled
            </span>
          </div>
        )}

        {bucket.openJobs === 0 ? (
          <div className="text-center text-muted-foreground py-1 text-xs">
            No open jobs for this week
          </div>
        ) : (
          <Collapsible open={expanded} onOpenChange={onToggle}>
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-between border-border text-xs h-8"
              >
                <span>
                  View {bucket.openJobs} open job{bucket.openJobs !== 1 ? "s" : ""}
                </span>
                {expanded ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-2">
              <div className="rounded-md border border-border overflow-hidden max-h-44 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center py-1 text-xs">PU Date</TableHead>
                      <TableHead className="text-center py-1 text-xs">Job ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bucket.openRows.map((row, idx) => (
                      <TableRow key={`${bucket.driver_name}-${row.Job_ID || "noid"}-${idx}`}>
                        <TableCell className="text-center whitespace-nowrap py-1 text-xs">
                          {formatDate(row.pu_date)}
                        </TableCell>
                        <TableCell className="text-center font-mono py-1 text-xs">
                          {row.Job_ID || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

const MiniMetric: React.FC<{
  label: string;
  value: string | number;
  tone?: "red" | "green" | "muted";
}> = ({ label, value, tone = "muted" }) => {
  const toneClass =
    tone === "red"
      ? "text-red-300"
      : tone === "green"
      ? "text-green-300"
      : "text-foreground";

  return (
    <div className="rounded-lg bg-black/20 border border-white/5 px-2 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm font-bold ${toneClass}`}>{value}</div>
    </div>
  );
};

const ActionPanel: React.FC<{
  title: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
}> = ({ title, icon, count, children }) => (
  <div className="rounded-2xl border border-border bg-card/60 p-4 shadow-md">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-semibold text-foreground">{title}</div>
      </div>
      <Badge className={count > 0 ? "bg-yellow-700 text-white" : "bg-green-700 text-white"}>
        {count}
      </Badge>
    </div>
    {children}
  </div>
);

const IssueList: React.FC<{
  tone: "red" | "orange" | "amber" | "yellow" | "blue" | "purple" | "cyan";
  title: string;
  /** `target` + `onSelect` make a row clickable (e.g. open the client). */
  items: { key: string; label: string; target?: string }[];
  onSelect?: (target: string) => void;
}> = ({ tone, title, items, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const toneClass =
    tone === "red"
      ? "text-red-300"
      : tone === "orange"
      ? "text-orange-300"
      : tone === "blue"
      ? "text-blue-300"
      : tone === "purple"
      ? "text-purple-300"
      : tone === "cyan"
      ? "text-cyan-300"
      : "text-yellow-300";

  return (
    <div className={`flex items-start gap-2 ${toneClass}`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-xs flex-1 min-w-0">
        <div className="font-semibold">{title}</div>
        {(expanded ? items : items.slice(0, 8)).map((item) =>
          item.target && onSelect ? (
            <button
              key={item.key}
              type="button"
              title="Open this client"
              onClick={() => onSelect(item.target as string)}
              className="block w-full text-left opacity-90 truncate hover:opacity-100 hover:underline cursor-pointer"
            >
              {item.label}
            </button>
          ) : (
            <div key={item.key} className="opacity-90 truncate">
              {item.label}
            </div>
          )
        )}
        {items.length > 8 && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="opacity-70 hover:opacity-100 underline cursor-pointer"
          >
            {expanded ? "Show fewer" : `…and ${items.length - 8} more — show all`}
          </button>
        )}
      </div>
    </div>
  );
};

const GroupedIssueList: React.FC<{
  tone: "red" | "orange" | "amber" | "yellow" | "blue" | "purple" | "cyan";
  title: string;
  groups: IssueGroup[];
}> = ({ tone, title, groups }) => {
  const toneClass =
    tone === "red"
      ? "text-red-300"
      : tone === "orange"
      ? "text-orange-300"
      : tone === "blue"
      ? "text-blue-300"
      : tone === "purple"
      ? "text-purple-300"
      : tone === "cyan"
      ? "text-cyan-300"
      : "text-yellow-300";

  return (
    <div className={`flex items-start gap-2 ${toneClass}`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-xs flex-1 min-w-0">
        <div className="font-semibold">{title}</div>
        {groups.slice(0, 8).map((group) => (
          <div key={group.key} className="mt-1.5 first:mt-0">
            <div className="opacity-90 truncate font-medium">{group.header}</div>
            {group.items.map((item) => (
              <div key={item.key} className="opacity-75 truncate pl-3">
                {item.label}
              </div>
            ))}
          </div>
        ))}
        {groups.length > 8 && (
          <div className="opacity-70 mt-1.5">…and {groups.length - 8} more</div>
        )}
      </div>
    </div>
  );
};

const EmptyMiniState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-xl border border-green-800/40 bg-green-950/20 p-3 text-sm text-green-300 flex items-center gap-2">
    <CheckCircle2 className="w-4 h-4" />
    {message}
  </div>
);

const LoadingMiniState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-xl border border-border bg-background/40 p-3 text-sm text-muted-foreground flex items-center gap-2">
    <Loader2 className="w-4 h-4 animate-spin" />
    {message}
  </div>
);

export default ClosedCheck;
